import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import './registration.scss';
import Steps from './Steps';
import { useForm, Controller } from 'react-hook-form';
import cx from 'classnames';
import Alert from '../utils/Alert';
import questionIcon from '../../assets/question-mark.svg';
import RegistrationContext from './context/RegistrationContext';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { AxiosPost } from '../http/Axios';

const Registration = () => {
  const { updateRegistrationData } = useContext(RegistrationContext);
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) {
      return digits.length ? `(${digits}` : '';
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const handleContinue = async (formData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (!executeRecaptcha) {
        throw new Error("reCAPTCHA not ready, please try again.");
      }

      const token = await executeRecaptcha('registration');

      const response = await AxiosPost('/kidlab-reg/families/registerAndLinkGuardian', {
          recaptchaToken: token,
          email: formData.email,
          zipCode: formData.zipCode,
          familyName: formData.lastName,
          referredBy: formData.howHeardAboutLab,
          guardianFirstName: formData.firstName,
          guardianLastName: formData.lastName,
          guardianEmail: formData.email,
          primaryPhone: formData.primaryPhone,
          relationshipToChild: formData.relationshipToChild,
      });

      if (response?.guid) {
           updateRegistrationData({ ...formData, guid: response.guid });
           window.scrollTo({ top: 0, behavior: 'smooth' });
           navigate('/children-step');
         } else {
           throw new Error(response?.message || 'Unexpected server response. Please try again.');
         }
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err.message || 'Unexpected error');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 1925 + 1 },
    (_, index) => currentYear - index
  );

  const relationshipOptions = [
    'Mother',
    'Father',
    'Grandmother',
    'Grandfather',
    'Sibling',
    'Cousin',
    'Stepmother',
    'Stepfather',
    'Aunt',
    'Uncle',
    'Nanny',
    'Foster Mother',
    'Foster Father',
    'Guardian',
    'Other',
  ];

  const referenceOptions = [
    'Craigslist',
    'LeapFrog Employee',
    'Friend',
    'Newspaper Ad',
    'Flyer',
    'Berkeley Parents Network',
    'Kindercare',
    'Golden Gate Mothers Group',
    '510 Families',
    'East Bay Preschool Fair',
    'Other',
  ];

  return (
    <div className="registration-page page-container">
      <div className="page-title">
        <h1>LeapFrog Lab Registration</h1>
      </div>
      <Steps activeItem={1} />

      {error && <Alert message={error} />}
      <div className="registration-form form-group">
        <h2 className="page-subtitle">Parent / Guardian Information</h2>
        <p className="required-notice">
          <span>*</span> Required Fields
        </p>
        <form onSubmit={handleSubmit(handleContinue)}>
          <div
            className={cx('field-group', {
              'validation-errors': errors.firstName,
            })}
          >
            <label htmlFor="firstName">
              <span>*</span>First name
            </label>
            <input
              type="text"
              maxLength={31}
              {...register('firstName', {
                required: true,
                pattern: /^[a-zA-Z .'"\-—]+$/i,
                maxLength: 31,
              })}
            />
            {errors.firstName && (
              <span className="error-label">
                {errors.firstName.type === 'required'
                  ? 'Please enter your first name'
                  : 'First name should only contain letters, period, apostrophe, hyphen/dash/em-dash, quotes, and spaces.'}
              </span>
            )}
          </div>

          <div
            className={cx('field-group', {
              'validation-errors': errors.lastName,
            })}
          >
            <label htmlFor="lastName">
              <span>*</span>Last name{' '}
            </label>
            <input
              type="text"
              maxLength={31}
              {...register('lastName', {
                required: true,
                pattern: /^[a-zA-Z .'"\-—]+$/i,
                maxLength: 31,
              })}
              title="Last name should only contain letters, period, apostrophe, hyphen/dash/em-dash, quotes, and spaces."
            />
            {errors.lastName && (
              <span className="error-label">
                {errors.lastName.type === 'required'
                  ? 'Please enter your last name'
                  : 'Last name should only contain letters, period, apostrophe, hyphen/dash/em-dash, quotes, and spaces.'}
              </span>
            )}
          </div>
          <div
            className={cx('field-group', { 'validation-errors': errors.email })}
          >
            <label htmlFor="email">
              <span>*</span>Email
            </label>

            <input
              id="email"
              type="text"
              maxLength={99}
              {...register('email', {
                required: true,
                pattern: {
                  value:
                    /^(?=.{1,99}$)(?!\.)(?!.*\.\.)([A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*)@([A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)(?:\.([A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?))+$/i,
                  message:
                    "Invalid email format (dot can't start/end or be consecutive; allowed specials: !#$%&'*+-/=?^_`{|}~).",
                },
                maxLength: {
                  value: 99,
                  message: 'Email must be ≤ 99 characters.',
                },
                setValueAs: (v) => (v ?? '').trim(), // optional: trim spaces
              })}
            />

            {errors.email && (
              <span className="error-label">
                {errors.email.type === 'required'
                  ? 'Please enter your email address.'
                  : errors.email.message}
              </span>
            )}
          </div>

          <div
            className={cx('field-group', {
              'validation-errors': errors.zipCode,
            })}
          >
            <label htmlFor="zipCode">
              <span>*</span>Zip code
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              maxLength={5}
              {...register('zipCode', {
                required: true,
                pattern: /^\d{5}$/,
                maxLength: 5,
              })}
              placeholder="#####"
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\D/g, '').slice(0, 5);
              }}
            />
            {errors.zipCode && (
              <span className="error-label">
                {errors.zipCode.type === 'required'
                  ? 'Please enter your zip code'
                  : 'Zip Code should contain 5 digits only.'}
              </span>
            )}
            <span className="bayarea">(Bay Area only)</span>
          </div>
          <div
            className={cx('field-group', {
              'validation-errors': errors.primaryPhone,
            })}
          >
            <label htmlFor="primaryPhone">
              <span>*</span>Primary phone{' '}
            </label>
            <Controller
              name="primaryPhone"
              control={control}
              rules={{
                required: true,
                pattern: /^\(\d{3}\) \d{3}-\d{4}$/i,
                maxLength: 14,
              }}
              render={({ field }) => (
                <input
                  type="tel"
                  id="primaryPhone"
                  value={field.value || ''}
                  onChange={(e) => {
                    const formattedValue = formatPhoneNumber(e.target.value);
                    field.onChange(formattedValue);
                  }}
                />
              )}
            />
            {errors.primaryPhone && (
              <span className="error-label">
                {errors.primaryPhone.type === 'required'
                  ? 'Please enter your primary phone number'
                  : 'Primary phone number must be in the format: (123) 456-7890.'}
              </span>
            )}
          </div>
          <div
            className={cx('field-group', {
              'validation-errors': errors.parentYearOfBirth,
            })}
          >
            <label htmlFor="parentYearOfBirth">
              <span>*</span> Parent year of birth
            </label>
            <div className="input-tooltip">
              <select
                {...register('parentYearOfBirth', {
                  required: true,
                  validate: (value) => {
                    const currentYear = new Date().getFullYear();
                    return (
                      currentYear - parseInt(value) >= 18 ||
                      'Parent must be at least 18 years old'
                    );
                  },
                })}
              >
                <option value="">Select Year</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <div className="info-icon">
                <img src={questionIcon} alt="LeapFrog" className="leapfrog" />
                <span className="info-text">
                  The Children's Online Privacy Protection Act (COPPA) requires
                  that we ask for your birth date. We do not save this
                  information.
                </span>
              </div>
            </div>
            {errors.parentYearOfBirth && (
              <span className="error-label">
                {errors.parentYearOfBirth.type === 'required'
                  ? 'Please enter your year of birth'
                  : errors.parentYearOfBirth.message}
              </span>
            )}
          </div>
          <div
            className={cx('field-group', {
              'validation-errors': errors.relationshipToChild,
            })}
          >
            <label htmlFor="relationshipToChild">
              <span>*</span>Relationship to child
            </label>
            <select {...register('relationshipToChild', { required: true })}>
              <option value="">Select Relationship</option>
              {relationshipOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.relationshipToChild && (
              <span className="error-label">
                {errors.relationshipToChild.type === 'required'
                  ? 'Please select your relationship to the child'
                  : 'Please enter your relationship to the child'}
              </span>
            )}
          </div>
          <div
            className={cx('field-group', {
              'validation-errors': errors.howHeardAboutLab,
            })}
          >
            <label htmlFor="howHeardAboutLab">
              <span>*</span>How you heard about the Lab?{' '}
            </label>
            <select {...register('howHeardAboutLab', { required: true })}>
              <option value="">Select Reference</option>
              {referenceOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.howHeardAboutLab && (
              <span className="error-label">
                {errors.howHeardAboutLab.type === 'required'
                  ? 'Please select how you heard about the Lab'
                  : 'Please select a reference'}
              </span>
            )}
          </div>
          <div className="field-group button">
            <button
              className="button primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Registration;
